import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { clearLegalAcceptanceRecord, hasAcceptedCurrentLegal, syncLegalAcceptanceFromProfile } from '../legal/acceptance';
import { authService } from '../services/api';
import { useFeatureStore, type FeatureMatrix } from '../auth/features';
import { clearSession, hasActiveSession, parseTokenExpiry } from '../utils/session';
import { Landing } from '../pages/Landing';

export function RequireAuth({
    children,
    requireLegal = true,
}: {
    children: React.ReactNode;
    requireLegal?: boolean;
}) {
    const token = localStorage.getItem('token');
    const location = useLocation();
    const [legalChecked, setLegalChecked] = useState(false);

    useEffect(() => {
        if (!token || !requireLegal) {
            setLegalChecked(true);
            return;
        }
        let cancelled = false;
        authService.getMe()
            .then((me: {
                legal_version?: string | null;
                legal_accepted_at?: string | null;
                role?: string | null;
                tier?: string | null;
                features?: FeatureMatrix | null;
            }) => {
                if (cancelled) return;
                // Tier + feature matrix for useFeature() (served by /auth/me).
                useFeatureStore.getState().setFromProfile(me);
                syncLegalAcceptanceFromProfile(me?.legal_version, me?.legal_accepted_at);
                setLegalChecked(true);
            })
            .catch((err: unknown) => {
                if (cancelled) return;
                const response = (err as { response?: { status?: number; data?: { detail?: { code?: string }; code?: string } } }).response;
                const code = response?.data?.detail?.code || response?.data?.code;
                if (response?.status === 403 && code === 'LEGAL_ACCEPTANCE_REQUIRED') {
                    clearLegalAcceptanceRecord();
                }
                setLegalChecked(true);
            });
        return () => { cancelled = true; };
    }, [token, requireLegal]);

    if (!token) {
        if (location.pathname === '/' || location.pathname === '') {
            return <Landing />;
        }
        return <Navigate to="/login" state={{ from: location }} replace />;
    }

    const expiresAt = parseTokenExpiry(token);

    if (expiresAt === null || expiresAt < Date.now()) {
        clearSession({ preservePublicCatalog: true });
        if (location.pathname === '/' || location.pathname === '') {
            return <Landing />;
        }
        return <Navigate to="/login" state={{ from: location, sessionExpired: true }} replace />;
    }

    if (requireLegal && !legalChecked) {
        return (
            <div className="min-h-screen bg-brand-page dark:bg-background" data-testid="legal-sync" />
        );
    }

    if (requireLegal && !hasAcceptedCurrentLegal()) {
        return <Navigate to="/legal/accept" state={{ from: location }} replace />;
    }

    return children;
}

/**
 * Utility: check if the current token is valid (for use outside React components).
 * Used by the 401 interceptor to avoid redundant redirects.
 */
export function isAuthValid(): boolean {
    return hasActiveSession();
}
